// I know 1 KB = 1024 bytes, but for user-friendliness, we use 1000 as the base for conversion to KB, MB, and GB.
export const formatBytes = (bytes: number): string => {
    if (bytes < 1000) return `${bytes} B`;
    if (bytes < 1000 ** 2) return `${(bytes / 1000).toFixed(1)} KB`;
    if (bytes < 1000 ** 3) return `${(bytes / 1000 ** 2).toFixed(1)} MB`;
    return `${(bytes / 1000 ** 3).toFixed(1)} GB`;
};

/**
 * Triggers a browser download for the specified URL by creating a hidden iframe.
 * The function resolves the promise when the download request has started, which is determined by either the iframe's load or error event, or a timeout.
 * After the download request has started, the iframe is cleaned up after a specified timeout.
 *
 * @remarks
 * This function is useful for triggering downloads of files that are served via an external URL (e.g. Dropbox) and may not have a Content-Disposition header set to "attachment".
 * It does not guarantee that the file has been fully downloaded, only that the download request has been initiated.
 *
 * @example
 * ```ts
 * triggerBrowserDownload('/path/to/file.pdf')
 *     .then(() => console.log('Download request started'))
 *     .catch((error) => console.error('Error starting download:', error));
 * ```
 * @param url The URL of the file to download.
 * @param requestStartedTimeoutMs The timeout in milliseconds to consider the download request started. Default is 1500 ms.
 * @param cleanupTimeoutMs The timeout in milliseconds to clean up the iframe. Default is 60000 ms.
 * @returns A promise that resolves when the download request has started.
 */
export const triggerBrowserDownload = (url: string, requestStartedTimeoutMs = 1500, cleanupTimeoutMs = 60000) =>
    new Promise<void>((resolve) => {
        const iframe = document.createElement('iframe');
        iframe.style.display = 'none';

        let resolved = false;
        const resolveRequestStart = () => {
            if (resolved) return;
            resolved = true;
            resolve();
        };

        const cleanup = () => {
            iframe.remove();
        };

        iframe.addEventListener(
            'load',
            () => {
                resolveRequestStart();
                cleanup();
            },
            { once: true }
        );
        iframe.addEventListener(
            'error',
            () => {
                resolveRequestStart();
                cleanup();
            },
            { once: true }
        );

        iframe.src = url;
        document.body.appendChild(iframe);

        window.setTimeout(resolveRequestStart, requestStartedTimeoutMs);
        window.setTimeout(cleanup, cleanupTimeoutMs);
    });
